import axios from "axios";
import { clearUserState } from "./auth/auth";
import composeRoute from "./routes/utils/route-utils/route-utils";
import { decodeJWT } from "@/utils/encoders/encoders";
import { checkSession } from "./auth/session";
import { ACCESS_DENIED_ROUTE } from "@/routing/app-routes";
import { getStoredToken } from "./auth/token-utils";

export const baseAxios = axios.create({
  baseURL: process.env.VITE_API_HOST,
  timeout: 10000,
});

export const longAxios = axios.create({
  baseURL: process.env.VITE_API_HOST,
  timeout: 30000,
});

/**
 * Adds a request interceptor to the provided Axios instance.
 * This interceptor will automatically attach the Bearer token from localStorage
 * to the Authorization header of every outgoing request.
 * @param {import("axios").AxiosInstance} instance The Axios instance to intercept.
 */
const addAuthInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = getStoredToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

addAuthInterceptor(baseAxios);
addAuthInterceptor(longAxios);

export const apiCall = (
  options = {
    method: "",
    url: "",
    data: undefined,
    params: undefined,
    headers: undefined,
    shouldRedirect: true,
    useLongAxios: false,
    multipart: false,
    responseType: undefined,
    signal: undefined,
  }
) => {
  try {
    const isJestEnvironment = false;
    const { responseType, useLongAxios, multipart, shouldRedirect, signal } =
      options;
    const token = getStoredToken();
    const { exp } = token ? decodeJWT(token) : { exp: 0 };
    const caller = useLongAxios && !isJestEnvironment ? longAxios : baseAxios;
    if (checkSession(exp, shouldRedirect)) {
      return new Promise((resolve, reject) => {
        const { method, url, params, data, headers } = options;

        // scub arguments
        const args = { method, url, params };
        if (data) {
          args.data = scrubRequest(data);
        }

        // Add signal to args if provided
        if (signal) {
          args.signal = signal;
        }

        // add in stand alone values
        if (responseType) {
          args.responseType = responseType;
        }
        args.url =
          (process.env.VITE_SERVER_URL || "http://localhost:3001") + url;
        args.headers = { ...caller.defaults.headers.common, ...headers };
        if (multipart) {
          // for multipart uploads, use this syntax instead to treat request form data as multipart
          caller
            .post(args.url, data, {
              params,
              signal,
              headers: { "Content-Type": "multipart/form-data" },
              ...headers,
            })
            .then((res) => {
              return resolve(res.data);
            })
            .catch((err) => {
              handleErrors(err);
              if (err.response) return reject(err.response.data);
              else return reject(err);
            });
        } else {
          return caller(args)
            .then((res) => {
              return resolve(res.data);
            })
            .catch((err) => {
              handleErrors(err);
              if (err.response) return reject(err.response.data);
              else return reject(err);
            });
        }
      });
    }
  } catch (error) {
    console.error(error);
  }
};

export const handleErrors = (err) => {
  console.error(err);
  if (err.statusCode === 403 || err.status === 403) {
    clearUserState();
    window.location.href = composeRoute(ACCESS_DENIED_ROUTE);
  }
};

const DISALLOWED_FIELDS = ["preSignedUrl"];

export const scrubRequest = (body) => {
  // Don't scrub File objects, Blob objects, or other binary data
  if (
    body instanceof File ||
    body instanceof Blob ||
    body instanceof ArrayBuffer
  ) {
    return body;
  }

  if (
    typeof body !== "object" ||
    body instanceof Date ||
    typeof body === "undefined" ||
    body === null
  )
    return body;

  if (Array.isArray(body)) {
    const scrubbed = [];
    body.forEach((value) => {
      if (typeof value === "object") {
        scrubbed.push(scrubRequest(value));
      } else {
        scrubbed.push(value);
      }
    });
    return scrubbed;
  } else if (typeof body === "object" && !!Object.prototype.toString.call) {
    const scrubbed = {};
    Object.keys(body).forEach((key) => {
      const value = body[key];
      if (typeof value === "object") {
        scrubbed[key] = scrubRequest(body[key]);
      } else if (!DISALLOWED_FIELDS.includes(key)) {
        scrubbed[key] = body[key];
      }
    });
    return scrubbed;
  }

  return body;
};
